import { useRef, useState, useEffect, ReactElement } from 'react'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import PageLayout from '../../components/page-layout/page-layout'
import PdImage from '../../components/pd-image/pd-image'
import retriveMessages, { LocaleType } from '../../messages/message'
import { usePUDUT300 } from '../../hooks/use-pudut300-data'
import WatchVideo from '../../components/pd-watch-video/pd-watch-video'
import { NextPageWithLayout } from '../../common/common-types'
import { GetStaticPropsContext } from 'next'
import React from 'react'
import PdSectionTitle from '../../components/pd-section-title/pd-section-title'
import StandardProductParamsPanel from '../../components/standard/commonModules/product-params-panel'
import { withSEO } from '../../common/with-seo'
import PdDisplaySwiperPanel from '../../components/pd-display-swiper-panel/pd-display-swiper-panel'
import { SwiperSlide } from 'swiper/react'
import { useRouter } from 'next/router'
import PdFloatRobot from '../../components/pd-float-robot/pd-float-robot'
import { useTranslations } from 'next-intl'
import ContactButton from '../../components/pd-contact-button/pd-contact-button'
import { buildContactUsHref } from '../../common/contact-us-link'
import TopTabSwitch from '../../components/standard/functionalModules/top-tab-switch'
import { IProductParamsPanelProps, Theme } from '../../common/pd-meta-components-types'

import { getProductMeta } from '../../common/seo'
import { EventReportManager } from '../../event-report/event-report-manager'

const DisplayCard: React.FC<{ name: string; mediaUrl: string }> = ({ name, mediaUrl }) => (
  <div className={['flex flex-col items-center w-full laptop:w-384px'].join(' ')}>
    <div className={['w-full h-[105.3vw] laptop:h-452px bg-[#242424]'].join(' ')}>
      <PdImage src={mediaUrl} enableMask={false} object="cover" width={'100%'} height={'100%'} />
    </div>
    <div
      className={[
        'w-full h-[25.3vw] px-3 laptop:h-112px laptop:px-6 flex items-center justify-center bg-[#1C1C1C]',
      ].join(' ')}
    >
      <span className="text-center text-white/90 laptop:text-xl text-base font-semibold">{name}</span>
    </div>
  </div>
)

const PUDUT300: NextPageWithLayout = () => {
  const data = usePUDUT300()
  gsap.registerPlugin(useGSAP, ScrollTrigger)
  const [isMobileDevice, setMobileDevice] = useState<boolean>(false)
  const [innerWidth, setInnerWidth] = useState<number>(0)
  const router = useRouter()
  const t = useTranslations()
  const reported = useRef(false)
  const variantParamsList = t.raw('pudut300.variantParams') as IProductParamsPanelProps[]

  useEffect(() => {
    setMobileDevice(window?.innerWidth < 1024)
    setInnerWidth(window?.innerWidth)
  }, [])

  useEffect(() => {
    const meta = getProductMeta(router.asPath)

    if (meta && !reported.current) {
      reported.current = true
      EventReportManager.getInstance().fireEventImmediately(
        {
          data: {
            event: 'view_item',
            item_id: meta.id,
            item_name: meta.name,
            item_category: meta.category,
          },
        },
        true
      )
    }
  }, [])

  return (
    <>
      {/* Section 1 */}
      <div
        style={{
          backgroundImage: isMobileDevice
            ? `url(${data.section1Data.backgroundImageH5Url})`
            : `url(${data.section1Data.backgroundImagePCUrl})`,
        }}
        className={['w-full bg-cover bg-no-repeat bg-center', 'h-[177.87vw]', 'laptop:h-[56.25vw]'].join(' ')}
      >
        <div className={['w-full h-full flex flex-col items-center', 'pt-15', 'laptop:pt-[4.16vw]'].join(' ')}>
          <div className={['w-76 h-10', 'laptop:w-[23.75vw] laptop:h-[3.125vw]'].join(' ')}>
            <PdImage
              src={data.section1Data.imageTitleUrl}
              object="cover"
              width="100%"
              height="100%"
              enableMask={false}
              priority={true}
            />
          </div>
          <span className={['text-white text-16 mt-3', ' laptop:text-2xl laptop:mt-4 '].join(' ')}>
            {data.section1Data.subTitle}
          </span>
          <div className="mt-6">
            <ContactButton className="px-6" href={buildContactUsHref('pudut300')} />
            <WatchVideo className={['ml-5'].join(' ')} videoUrl={data.section1Data.videoUrl} />
          </div>
        </div>
      </div>
      {/* Variant Tabs */}
      <TopTabSwitch {...data.sectionVariantTabsData} />
      {/* Section 2 */}
      <div className={['w-full laptop:pt-30 laptop:pb-82px bg-[#141414]'].join(' ')}>
        <div className={['pt-15 px-6', 'laptop:pt-0 laptop:px-0 laptop:w-1080px laptop:mx-auto'].join(' ')}>
          <PdSectionTitle
            title={data.section2Data.title}
            subTitle={data.section2Data.subTitle}
            themeColor={'white90'}
            align={isMobileDevice ? 'left' : 'center'}
          />
        </div>
        <div className={['w-full h-[40vw] laptop:w-300 laptop:box-content laptop:h-120 laptop:mx-auto'].join(' ')}>
          <PdImage
            src={data.section2Data.backgroundImagePCUrl}
            enableMask={false}
            object="contain"
            width={'100%'}
            height={'100%'}
          />
        </div>
      </div>
      {/* Section 3 */}
      {false && (
        <div
          className={['w-full laptop:h-[calc(100vh-64px)] laptop:max-h-860px laptop:relative laptop:pt-30'].join(' ')}
        >
          <div className={['absolute top-30 left-0 right-0 z-20'].join(' ')}>
            <PdSectionTitle
              title={data.section3Data.title}
              subTitle={data.section3Data.subTitle}
              themeColor={'white90'}
            />
          </div>
          <div className={['absolute top-0 bottom-0 left-0 right-0 w-full h-full bg-cyan-800'].join(' ')}></div>
        </div>
      )}
      {/* Section 4 */}
      <div className={['bg-[#141414] w-full'].join(' ')}>
        {false && (
          <div className={['px-6 py-15 laptop:px-0 laptop:py-30 '].join(' ')}>
            <PdSectionTitle
              title={data.section4Data.title}
              themeColor={'white90'}
              align={isMobileDevice ? 'left' : 'center'}
            />
            <div
              className={[
                'w-full grid grid-cols-2 gap-3',
                'laptop:w-300 laptop:mx-auto laptop:grid-cols-4 laptop:gap-4',
              ].join(' ')}
            >
              {data.section4Data.features.map((item, index) => (
                <div
                  key={index}
                  className={[
                    'hover:bg-[#5A5A5A] bg-[#282828] py-5 px-2 flex flex-col items-center gap-2',
                    'laptop:py-10 laptop:px-5 laptop:gap-0',
                  ].join(' ')}
                >
                  <div className={['bg-[gray]/90 w-10 h-10', 'laptop:w-20 laptop:h-20 laptop:mb-6'].join(' ')}></div>
                  <h1
                    className={[
                      'text-white font-semibold font-["Montserrat"] text-24',
                      'laptop:text-48 laptop:mb-6px',
                    ].join(' ')}
                  >
                    {item.value}
                  </h1>
                  <span
                    className={['text-center text-white text-16 font-semibold', 'laptop:text-20 laptop:my-4'].join(' ')}
                  >
                    {item.title}
                  </span>
                  <span className={['text-center text-white text-14', 'laptop:text-14'].join(' ')}>{item.desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <div
          id="t300-section4-part2"
          className={[
            'laptop:w-300 laptop:mx-auto laptop:py-30 laptop:px-0',
            'w-full px-6 py-15 flex flex-wrap relative',
          ].join(' ')}
        >
          <div
            className={[
              'flex flex-wrap gap-x-3 gap-y-6',
              'laptop:flex-col laptop:justify-center laptop:w-144 laptop:gap-12',
            ].join(' ')}
          >
            {data.section4Data.attributes.map((item, index) => (
              <div
                key={index}
                className={[
                  isMobileDevice && [2, 3].includes(index) && 'basis-full',
                  'flex flex-col gap-1 w-157px',
                  'laptop:w-full laptop:gap-2',
                ].join(' ')}
              >
                <span
                  className={[
                    "text-white font-['Montserrat'] text-28 font-semibold",
                    'laptop:text-5xl laptop:leading-[56px]',
                  ].join(' ')}
                >
                  {item.value}
                </span>
                <span className={['text-white/60 laptop:text-14', 'laptop:text-2xl laptop:leading-loose'].join(' ')}>
                  {item.desc}
                </span>
              </div>
            ))}
          </div>
          <div
            id="t300-section4-part2-robot"
            className={[
              'relative w-72 h-450px mt-16',
              // 'laptop:absolute laptop:w-144 laptop:h-900px laptop:top-125 laptop:left-591px laptop:mt-0',
              'laptop:absolute laptop:w-144 laptop:h-900px laptop:top-0 laptop:left-591px laptop:mt-0',
            ].join(' ')}
          >
            <PdImage
              src={data.section4Data.robotImg.url}
              enableMask={false}
              object="cover"
              width={'100%'}
              height={'100%'}
            />
            {[
              {
                value: data.section4Data.robotImg.width,
                position: 'bottom-1 left-7 laptop:bottom-8 laptop:left-62px',
              },
              {
                value: data.section4Data.robotImg.length,
                position: 'bottom-5 right-4 laptop:bottom-65px laptop:right-62px',
              },
              {
                value: data.section4Data.robotImg.height,
                position: 'top-[42%] -right-11 laptop:top-[42%] laptop:-right-10',
              },
            ].map((item, index) => (
              <div
                key={index}
                className={[
                  item.position,
                  'absolute z-20 text-white/60 text-14 font-["Source Han Sans CN"]',
                  'laptop:text-base laptop:leading-normal',
                ].join(' ')}
              >
                {item.value}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section 5 */}
      <div className={['w-full bg-[#141414] py-15 px-6', 'laptop:px-0 laptop:py-30'].join(' ')}>
        <div className={['laptop:w-860px laptop:mx-auto'].join(' ')}>
          <PdSectionTitle
            title={data.section5Data.title}
            subTitle={data.section5Data.subTitle}
            themeColor={'white90'}
            align={isMobileDevice ? 'left' : 'center'}
          />
        </div>
        <div
          className={['grid grid-cols-1 gap-15', 'laptop:w-300 laptop:mx-auto laptop:grid-cols-3 laptop:gap-4'].join(
            ' '
          )}
        >
          {data.section5Data.feature.map((item, index) => (
            <div key={index} className={['flex flex-col'].join(' ')}>
              <div className={['bg-[#282828] laptop:h-105 h-[95.5vw] w-full'].join(' ')}>
                <PdImage src={item.imgUrl} enableMask={false} object="contain" width={'100%'} height={'100%'} />
              </div>
              <h3
                className={[
                  'mt-5 text-white text-opacity-90 font-semibold text-24',
                  'laptop:mt-6 laptop:text-2xl laptop:leading-loose',
                ].join(' ')}
              >
                {item.title}
              </h3>
              <p
                className={[
                  'mt-3 text-white text-opacity-90 text-16',
                  'laptop:mt-2 laptop:text-sm laptop:leading-snug',
                ].join(' ')}
              >
                {item.desc}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Section 6 */}
      <div className={['w-full bg-[#141414] py-15 px-6 laptop:px-0 laptop:py-30 laptop:pb-0'].join(' ')}>
        <div className={['w-full laptop:w-200 laptop:mx-auto'].join(' ')}>
          <PdSectionTitle
            title={data.section6Data.title}
            subTitle={data.section6Data.subTitle}
            themeColor={'white90'}
            align={isMobileDevice ? 'left' : 'center'}
          />
        </div>
        {isMobileDevice ? (
          <div className={['w-full'].join(' ')}>
            {data.section6Data.feature.length && (
              <PdDisplaySwiperPanel
                autoplay={{
                  delay: 3000,
                  disableOnInteraction: false,
                }}
                grabCursor
                normalizeSlideIndex={false}
                slidesPerView={1}
                spaceBetween={0}
                pagination={{
                  el: '.pd-display-swiper-pagination1',
                  clickable: true,
                  dynamicBullets: true,
                }}
                navigation={false}
                elClassName={{
                  elClassName: ['absolute z-30 h-2 w-full bottom-10'].join(' '),
                  paginationClassName: [
                    'pd-display-swiper-pagination1 pd-pagination-container flex items-center justify-center',
                  ].join(' '),
                }}
                slidesOffsetBefore={isMobileDevice ? 0 : (innerWidth - 1200) / 2}
              >
                {data.section6Data.feature?.map((item, index) => (
                  <SwiperSlide key={index} className="w-full laptop:!w-fit">
                    <div className={['w-full h-[115.2vw] bg-[#282828] mb-3'].join(' ')}>
                      <PdImage src={item.imgUrl} enableMask={false} object="cover" width={'100%'} height={'100%'} />
                    </div>
                    <h3 className="text-center text-base font-semibold text-white/90">{item.title}</h3>
                    <p className="text-center text-base text-white/90">{item.desc}</p>
                  </SwiperSlide>
                ))}
              </PdDisplaySwiperPanel>
            )}
          </div>
        ) : (
          <div className={['laptop:w-full laptop:bg-none laptop:grid laptop:grid-cols-4 laptop:gap-2'].join(' ')}>
            {data.section6Data.feature.map((item, index) => (
              <div key={index} className={['bg-[#1c1c1c] flex flex-col'].join(' ')}>
                <h3
                  className={['text-white text-opacity-90 text-2xl font-semibold mt-15 w-full text-center'].join(' ')}
                >
                  {item.title}
                </h3>
                <p className={['mt-2 text-white text-opacity-90 text-sm font-normal leading-snug mx-10'].join(' ')}>
                  {item.desc}
                </p>
                <div className={['h-[29.17vw] w-full mt-57px'].join(' ')}>
                  <PdImage src={item.imgUrl} enableMask={false} object="cover" width={'100%'} height={'100%'} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div id="t300-section7-scrollbox" className={['w-full relative h-[100vh]'].join(' ')}>
        {/* Section 7 */}
        <div
          id="t300-section7"
          className={['w-full h-[100vh] overflow-hidden absolute top-0 left-0 right-0 z-20'].join(' ')}
        >
          <div
            id="t300-section7-bg"
            style={{
              backgroundImage: `url(${data.section7Data.imgUrl})`,
            }}
            // className={['w-full h-full scale-200 absolute top-0 left-0 right-0 bg-cover bg-no-repeat bg-center'].join(
            //   ' '
            // )}
            className={['w-full h-full absolute top-0 left-0 right-0 bg-cover bg-no-repeat bg-center'].join(' ')}
          ></div>
          <div
            className={[
              'relative z-20 w-full bg-gradient-to-b from-[#141414] h-[50vh] py-15 px-6',
              'laptop:pt-30 laptop:px-0',
            ].join(' ')}
          >
            <div className={['w-full laptop:w-1080px laptop:mx-auto'].join(' ')}>
              <PdSectionTitle
                themeColor={'white90'}
                title={data.section7Data.title}
                subTitle={data.section7Data.subTitle}
                subTitleBoxStyle={'w-full laptop:w-200'}
                align={isMobileDevice ? 'left' : 'center'}
              />
            </div>
          </div>
        </div>
        {/* Section 8 */}
        {false && (
          <div
            id="t300-section8"
            className={['relative z-20 w-full bg-[#141414] py-15 px-6', 'laptop:px-0 laptop:py-30'].join(' ')}
          >
            <div className={['w-full', 'laptop:w-300 laptop:mx-auto'].join(' ')}>
              <PdSectionTitle
                title={data.section8Data.title}
                subTitle={data.section8Data.subTitle}
                themeColor={'white90'}
                align={isMobileDevice ? 'left' : 'center'}
              />
              <div
                className={['w-full grid grid-cols-2 gap-3', 'laptop:grid laptop:grid-cols-3 laptop:gap-6'].join(' ')}
              >
                {/* pc 排版 */}
                {!isMobileDevice &&
                  data.section8Data.features.map((item, index) =>
                    index ? (
                      <div
                        key={index}
                        className={['col-span-1 bg-[#1C1C1C] p-10 flex flex-col items-center gap-4'].join(' ')}
                      >
                        <div className={['w-20 h-20 bg-[#5A5A5A]'].join(' ')}>
                          <PdImage
                            src={item.mediaUrl}
                            object="cover"
                            width="100%"
                            height="100%"
                            enableMask={false}
                            priority={true}
                          />
                        </div>
                        <h3 className={['text-center text-white text-xl font-semibold leading-7'].join(' ')}>
                          {item.name}
                        </h3>
                        <p className={['text-center text-white text-sm font-normal leading-snug'].join(' ')}>
                          {item.desc}
                        </p>
                      </div>
                    ) : (
                      <div key={index} className={['col-span-3 h-675px bg-[#1C1C1C]'].join(' ')}>
                        <PdImage
                          src={item.mediaUrl}
                          object="cover"
                          width="100%"
                          height="100%"
                          enableMask={false}
                          priority={true}
                        />
                      </div>
                    )
                  )}
                {/* h5 排版 */}
                {isMobileDevice &&
                  data.section8Data.features.map((item, index) =>
                    index ? (
                      <div
                        key={index}
                        className={[
                          index === 1 ? 'col-span-2 ' : 'col-span-1',
                          'bg-[#1C1C1C] py-5 px-2 flex flex-col items-center',
                        ].join(' ')}
                      >
                        <div className={['w-10 h-10 bg-[#5A5A5A]'].join(' ')}>
                          <PdImage
                            src={item.mediaUrl}
                            object="cover"
                            width="100%"
                            height="100%"
                            enableMask={false}
                            priority={true}
                          />
                        </div>
                        <h3 className={['text-center text-white text-base font-semibold mt-3 mb-2'].join(' ')}>
                          {item.name}
                        </h3>
                        <p className={['text-center text-white text-14'].join(' ')}>{item.desc}</p>
                      </div>
                    ) : (
                      <div
                        key={index}
                        style={{
                          aspectRatio: '1 / 1',
                        }}
                        className={['col-span-2 bg-[#1C1C1C]'].join(' ')}
                      >
                        <PdImage
                          src={item.mediaUrl}
                          object="cover"
                          width="100%"
                          height="100%"
                          enableMask={false}
                          priority={true}
                        />
                      </div>
                    )
                  )}
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Section 9 */}
      <div
        id="t300-section9"
        className={['relative z-30 w-full bg-[#141414] py-15 px-6 laptop:px-0 laptop:py-30'].join(' ')}
      >
        <div className={['w-full', 'laptop:w-840px laptop:mx-auto'].join(' ')}>
          <PdSectionTitle
            title={data.section9Data.title}
            subTitle={data.section9Data.subTitle}
            themeColor={'white90'}
            align={isMobileDevice ? 'left' : 'center'}
          />
        </div>
        <div
          style={{
            aspectRatio: isMobileDevice ? '1 / 1' : '',
          }}
          className={['w-full', 'laptop:h-675px laptop:w-300 laptop:mx-auto'].join(' ')}
        >
          <PdImage src={data.section9Data.mediaUrl} enableMask={false} object="cover" width={'100%'} height={'100%'} />
        </div>
      </div>
      {/* Section 10 */}
      <div className={['relative z-20 w-full bg-[#141414] px-6 py-15 laptop:px-0 laptop:py-30'].join(' ')}>
        <div
          className={[
            'laptop:w-300 laptop:mx-auto laptop:grid laptop:grid-cols-2 laptop:grid-rows-2 laptop:gap-x-6 laptop:gap-y-16',
          ].join(' ')}
        >
          {data.section10Data.feature
            .sort((a, b) => {
              return isMobileDevice ? a.h5Rank - b.h5Rank : a.pcRank - b.pcRank
            })
            .map((item, index) => (
              <div
                key={index}
                className={[isMobileDevice && item.h5Rank === 2 && 'mb-30', 'laptop:h-160', 'w-full'].join(' ')}
              >
                {item.type === 'media' ? (
                  <div
                    style={{
                      aspectRatio: isMobileDevice ? '1 / 1' : '',
                    }}
                    className={['w-full', 'laptop:h-full'].join(' ')}
                  >
                    <PdImage
                      src={item.mediaUrl || ''}
                      enableMask={false}
                      object="contain"
                      width={'100%'}
                      height={'100%'}
                    />
                  </div>
                ) : (
                  <div
                    className={[
                      'w-full h-full',
                      'laptop:flex laptop:justify-end laptop:items-center',
                      index === 1 ? 'laptop:pl-25' : 'laptop:pl-72px laptop:pr-30px',
                    ].join(' ')}
                  >
                    <PdSectionTitle
                      subTitleSize={'laptop:text-lg text-base'}
                      align={'left'}
                      title={item?.title ?? ''}
                      subTitle={item?.subTitle}
                      themeColor={'white90'}
                    />
                  </div>
                )}
              </div>
            ))}
        </div>
      </div>
      {/* Section 11 */}
      <div className={['relative z-20 w-full bg-[#141414] laptop:py-30 laptop:px-0 py-15 px-6'].join(' ')}>
        <div className={['laptop:w-840px laptop:mx-auto'].join(' ')}>
          <PdSectionTitle
            title={data.section11Data.title}
            subTitle={data.section11Data.subTitle}
            align={isMobileDevice ? 'left' : 'center'}
            themeColor={'white90'}
          />
        </div>
        <div
          className={['w-full grid gap-15', 'laptop:w-300 laptop:mx-auto laptop:grid-cols-2 laptop:gap-6'].join(' ')}
        >
          {data.section11Data.features.map((item, index) => (
            <div key={index} className={['w-full flex flex-col items-center gap-8 laptop:gap-4'].join(' ')}>
              <div
                style={{
                  aspectRatio: isMobileDevice ? '1 / 1' : '',
                }}
                className={['w-full laptop:h-150'].join(' ')}
              >
                <PdImage src={item.mediaUrl || ''} enableMask={false} object="contain" width={'100%'} height={'100%'} />
              </div>
              <p
                className={[
                  'w-full text-left text-white font-semibold text-24',
                  'laptop:text-center laptop:text-2xl laptop:leading-loose',
                ].join(' ')}
              >
                {item.name}
              </p>
            </div>
          ))}
        </div>
      </div>
      {/* Section 12 */}
      <div className={['relative z-20 w-full bg-[#141414] py-15 px-6 laptop:px-0 laptop:py-30'].join(' ')}>
        <div className={['laptop:w-840px laptop:mx-auto'].join(' ')}>
          <PdSectionTitle
            title={data.section12Data.title}
            subTitle={data.section12Data.subTitle}
            themeColor={'white90'}
            align={isMobileDevice ? 'left' : 'center'}
          />
        </div>
        <div
          className={[
            'w-full grid grid-cols-2 gap-3',
            'laptop:w-300 laptop:mx-auto laptop:grid-cols-3 laptop:grid-rows-2 laptop:gap-6',
          ].join(' ')}
        >
          {data.section12Data.features.map((item, index) => (
            <div
              key={index}
              style={{
                aspectRatio: isMobileDevice && !index ? '1 / 1' : '',
              }}
              className={[
                index ? 'laptop:col-span-1 h-55' : 'laptop:col-span-3',
                isMobileDevice && ([2, 3].includes(index) ? 'col-span-1' : 'col-span-2'),
                'overflow-hidden relative bg-[#282828]',
                'laptop:h-105',
              ].join(' ')}
            >
              <PdImage
                src={isMobileDevice ? item.mediaUrlH5 : item.mediaUrl || ''}
                enableMask={false}
                object="cover"
                width={'100%'}
                height={'100%'}
              />
              <p
                className={[
                  'absolute w-full top-0 left-0 p-4 text-left text-base text-white font-semibold break-words',
                  'laptop:text-xl laptop:leading-7 laptop:p-6 laptop:break-words',
                ].join(' ')}
              >
                {item.name}
              </p>
            </div>
          ))}
        </div>
      </div>
      {/* Section 13 */}
      <div
        className={['relative z-20 w-full bg-[#141414] py-15 px-6 pb-30 laptop:pb-60 laptop:px-0 laptop:py-30'].join(
          ' '
        )}
      >
        <div className={['w-full laptop:w-200 laptop:mx-auto'].join(' ')}>
          <PdSectionTitle
            title={data.section13Data.title}
            subTitle={data.section13Data.subTitle}
            themeColor={'white90'}
            align={isMobileDevice ? 'left' : 'center'}
          />
        </div>
        {data.section13Data.features?.length && (
          <PdDisplaySwiperPanel
            autoplay={{
              delay: 3000,
              disableOnInteraction: false,
            }}
            slidesPerGroup={1}
            loop={true}
            loopedSlides={data.section13Data.features?.length}
            grabCursor
            slidesPerView={isMobileDevice ? 1 : 'auto'}
            spaceBetween={isMobileDevice ? 0 : 24}
            pagination={{
              el: '.pd-display-swiper-pagination',
              clickable: true,
              dynamicBullets: true,
            }}
            navigation={
              !isMobileDevice
                ? {
                    nextEl: '.pd-display-swiper-next',
                    prevEl: '.pd-display-swiper-prev',
                  }
                : false
            }
            elClassName={{
              elClassName: [
                'absolute w-full h-2 -bottom-8 ',
                ' laptop:!w-300 laptop:h-25 laptop:mx-auto laptop:-bottom-25 laptop:left-[calc((100vw-1200px)/2)]',
              ].join(' '),
              paginationClassName: [
                'pd-display-swiper-pagination pd-pagination-container absolute flex items-center justify-center laptop:justify-start',
                'laptop:h-14 laptop:bottom-0',
              ].join(' '),
              nextElClassName: [
                'pd-display-swiper-next absolute cursor-pointer bg-center rotate-180',
                ' bottom-0 right-0 laptop:h-14 laptop:w-14 bg-white/20 bg-swiperBtn',
              ].join(' '),
              prevElClassName: [
                'pd-display-swiper-prev absolute cursor-pointer bg-center',
                ' bottom-0 right-20 laptop:h-14 laptop:w-14 bg-white/20 bg-swiperBtn',
              ].join(' '),
            }}
            slidesOffsetBefore={isMobileDevice ? 0 : (innerWidth - 1200) / 2}
          >
            {data.section13Data.features?.map((item, index) => (
              <SwiperSlide key={index} className="w-full laptop:!w-fit">
                <DisplayCard {...item} />
              </SwiperSlide>
            ))}
          </PdDisplaySwiperPanel>
        )}
      </div>
      {/* parameter */}
      <div className={['relative z-20'].join(' ')}>
        {variantParamsList.map((params) => (
          <StandardProductParamsPanel key={params.title} {...params} theme={Theme.Dark} />
        ))}
      </div>
      <PdFloatRobot
        robot={'https://cdn.pudutech.com/official-website/product/float_btn/pudut300-float.png'}
        color="#0066ff"
        href={buildContactUsHref('pudut300')}
        hideCircleBorder={true}
        buttonText={t('RequestDemo')}
        buttonTextSize="small"
        buttonTextColor="#fff"
      />
    </>
  )
}

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['products'], locale as LocaleType)

    return {
      props: {
        messages,
      },
      revalidate: 60,
    }
  },
  () => '/products/pudut300'
)

PUDUT300.getLayout = function (page: ReactElement, seo) {
  return (
    <PageLayout
      sticky={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </PageLayout>
  )
}

export default PUDUT300
